import { DisconnectOutlined, LinkOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { App, Button, Descriptions, Drawer, Flex, Select, Space, Typography, theme } from 'antd';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import { useEcardStore } from '../../hooks/store/useEcardStore.ts';
import { useDoctorEcardEnabled } from '../../hooks/useDoctorEcardEnabled.ts';
import type { UpdateEvent } from '../../socket/types.ts';
import { useSubscription } from '../../socket/useSubscription.ts';
import { buildErrorDetails, ecardEnv, getCardSlot } from '../../utils/ginoHelpers.ts';
import { hasSomeRole, Role } from '../../utils/user.ts';
import { type ConnectionState, EcardConnectionCheck } from '../settings/ecard/EcardConnectionCheck.tsx';
import { ECARD_ACTIONS_QUERY } from './EcardActions.gql';

export const EcardActions: React.FC = () => {
  const auth = useAuth();
  const { token } = theme.useToken();
  const isUserRole = hasSomeRole([Role.ROLE_LR_USER], auth.user);
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const doctorId = isUserRole ? currentDoctorId : primaryDoctorId;
  const [open, setOpen] = useState(false);
  const [configId, setConfigId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { dialog, config, berechtigungen, clearState, setState, closeDialogRequested } = useEcardStore();
  const [connectionState, setConnectionState] = useState<ConnectionState>('pending');
  const { notification } = App.useApp();

  const { data, refetch } = useQuery(ECARD_ACTIONS_QUERY, {
    variables: {
      doctorId,
    },
    skip: !doctorId,
  });
  // Same doctorId that feeds the query above, so the feature flag and the configs match.
  const ecardEnabled = useDoctorEcardEnabled(doctorId);

  const callback = useCallback(
    (event: UpdateEvent) => {
      console.debug(`received UI update: ${JSON.stringify(event)}`);
      refetch();
    },
    [refetch]
  );

  useSubscription({ type: 'ecard-config-created', tenantId: doctorId }, callback);
  useSubscription({ type: 'ecard-config-deleted', tenantId: doctorId }, callback);

  const closeDialog = useCallback(async () => {
    if (!dialog?.dialogId) {
      return;
    }

    setLoading(true);
    try {
      // execute silently
      await window.nativeApi?.ecardCloseDialog(ecardEnv(), dialog.dialogId);
    } finally {
      clearState();
      setConfigId(null);
      setLoading(false);
    }
  }, [clearState, dialog]);

  /** close the dialog when there is no more valid config available, e.g. on deletion of a config */
  useEffect(() => {
    if (!data?.ecardConfigs || !config) {
      return;
    }
    if (!data.ecardConfigs.find(it => it.id === config.id)) {
      closeDialog();
    }
  }, [data, config, closeDialog]);

  /** close the dialog when the tab/browser/window gets closed/reloaded **/
  useEffect(() => {
    const listener = async () => {
      await closeDialog();
    };
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  }, [closeDialog]);

  /** close the dialog when it's requested via the state store */
  useEffect(() => {
    if (closeDialogRequested) {
      closeDialog();
    }
  }, [closeDialogRequested, closeDialog]);

  /** close the dialog when component gets unmounted from the DOM */
  // biome-ignore lint/correctness/useExhaustiveDependencies: known no-op — captures the render-0 closeDialog (dialog still null); adding it would close the dialog mid-use, needs the latest-ref pattern
  useEffect(() => {
    // on unmount from DOM
    return () => {
      closeDialog();
    };
  }, []);

  /** periodically checks the dialog status and closes it if necessary */
  useEffect(() => {
    if (!dialog?.dialogId) {
      return;
    }

    const dialogId = dialog.dialogId;

    const interval = setInterval(async () => {
      console.debug('checking e-card dialog status');
      try {
        const response = await window.nativeApi?.ecardBerechtigungen(ecardEnv(), dialogId);

        if (response?.error?.type === 'DialogException') {
          console.warn(`e-card dialog is closed due to dialog exception: ${buildErrorDetails(response)}`);
          await closeDialog();
          notification.warning({
            title: 'e-card Dialog beendet',
            description: 'Der e-card Dialog ist abgelaufen oder wurde unerwartet beendet. Bitte verbinden Sie sich erneut.',
          });
        }
      } catch (err) {
        console.error(`error while checking e-card dialog status: ${err}`);
        await closeDialog();
        notification.warning({
          title: 'e-card Dialogfehler',
          description: 'Beim Überprüfen des e-card Dialogs ist ein Fehler aufgetreten. Der Dialog wurde geschlossen.',
        });
      }
    }, 900000); // check all 15 minutes

    return () => clearInterval(interval);
  }, [dialog, closeDialog, notification]);

  const onOpen = () => {
    setOpen(true);
    setError('');
  };

  const onClose = () => {
    setOpen(false);
    setError('');
  };

  const createDialog = async () => {
    setError('');

    const selectedConfig = data?.ecardConfigs?.find(it => it.id === configId);
    if (!selectedConfig) {
      setError('Konfiguration nicht gefunden');
      return;
    }

    setLoading(true);
    try {
      const response = await window.nativeApi!.ginoStatus(selectedConfig.cardReaderAddress, {
        sigType: 'vp',
        vpnr: selectedConfig.vpnr,
        pin: selectedConfig.cardPin,
      });

      if (response.error) {
        setError(`Kartenleser-Fehler: ${buildErrorDetails(response)}`);
        return;
      }

      const slot = getCardSlot(response, 'o-card');
      const token = slot?.token?.value;
      if (!token) {
        setError('Kartenleser-Fehler: Es konnte keine gültige Signatur ausgestellt werden. Eine Admin-Karte (o-card) ist erforderlich.');
        return;
      }

      const dialogResponse = await window.nativeApi?.ecardCreateDialog(ecardEnv(), token, auth.user?.profile.sub ?? 'unknown');

      if (dialogResponse?.error) {
        setError(`Fehler beim Erzeugen des Dialogs: ${buildErrorDetails(dialogResponse)}`);
        return;
      }

      const dialogData = dialogResponse?.data?.return;
      if (!dialogData?.dialogId) {
        setError('Fehler beim Lesen der Dialogdaten');
        return;
      }

      const addressResponse = await window.nativeApi?.ecardSetDialogAddress(ecardEnv(), {
        dialogId: dialogData.dialogId,
        ordinationId: selectedConfig.ordinationId,
        taetigkeitsBereichId: selectedConfig.taetigkeitsBereichId,
      });

      if (addressResponse?.error) {
        setError(`Fehler beim Setzen des Standorts der Ordination: ${buildErrorDetails(addressResponse)}`);
        return;
      }

      const berechtigungenResponse = await window.nativeApi?.ecardBerechtigungen(ecardEnv(), dialogData.dialogId);

      if (berechtigungenResponse?.error) {
        setError(`Fehler beim Laden der Berechtigungen: ${buildErrorDetails(berechtigungenResponse)}`);
        return;
      }

      const berechtigungen = berechtigungenResponse?.data?.return ?? [];
      setState(dialogData, selectedConfig, berechtigungen);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!ecardEnabled || !data?.ecardConfigs?.length || !window.nativeApi) {
    return null;
  }

  const ordi = dialog?.vp?.ordination?.find(it => it.ordinationId === config?.ordinationId);

  return (
    <>
      <Button
        type="text"
        icon={
          dialog ? (
            <div
              className={css`
                width: 12px;
                height: 12px;
                background-color: ${token.colorSuccess};
                border-radius: 100%;
              `}
            />
          ) : (
            <div
              className={css`
                width: 12px;
                height: 12px;
                background-color: ${token.colorError};
                border-radius: 100%;
              `}
            />
          )
        }
        onClick={onOpen}
      >
        e-card
      </Button>
      <Drawer
        title="e-card"
        placement="right"
        destroyOnHidden={true} // let's re-execute the connection check
        open={open}
        size="50%"
        rootClassName={css`
          .ant-drawer-content-wrapper {
            max-width: 800px;
            min-width: 500px;
          }
        `}
        onClose={onClose}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          {dialog ? (
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Descriptions column={1} size="small" bordered styles={{ label: { width: '250px' } }}>
                <Descriptions.Item label="VP-Nummer">{dialog.vp?.vpNummer}</Descriptions.Item>
                <Descriptions.Item label="Tätigkeitsbereich">{config?.taetigkeitsBereichName}</Descriptions.Item>
                <Descriptions.Item label="Ordination">
                  {ordi?.strasse}, {ordi?.postleitzahl} {ordi?.stadt}
                </Descriptions.Item>
                {/*<Descriptions.Item label="Anrede">{dialog.vp?.anredeCode}</Descriptions.Item>*/}
                <Descriptions.Item label="Titel Vorne">{dialog.vp?.titelVorne}</Descriptions.Item>
                <Descriptions.Item label="Vorname">{dialog.vp?.vorname}</Descriptions.Item>
                <Descriptions.Item label="Nachname">{dialog.vp?.nachname}</Descriptions.Item>
                <Descriptions.Item label="Titel Hinten">{dialog.vp?.titelHinten}</Descriptions.Item>
                {/*<Descriptions.Item label="Geburtsdatum">{dialog.vp?.geburtsdatum}</Descriptions.Item>*/}
                <Descriptions.Item label="Letzte Anmeldung">{dialog.vp?.authenticationStatus?.lastLoginDate}</Descriptions.Item>
                <Descriptions.Item label="Adresse Kartenleser (GINO)">
                  <span className={monoFont}>{config?.cardReaderAddress}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Berechtigungen">{berechtigungen.join(', ')}</Descriptions.Item>
              </Descriptions>
              <Button type="primary" onClick={closeDialog} loading={loading} icon={<DisconnectOutlined />}>
                Dialog beenden
              </Button>
            </Space>
          ) : (
            <Space orientation="vertical" style={{ width: '100%' }}>
              <EcardConnectionCheck connectionState={connectionState} onConnectionStateChanged={setConnectionState} />
              {connectionState === 'ok' && (
                <Flex gap="small">
                  <Select
                    optionLabelProp="taglabel"
                    options={data.ecardConfigs.map(it => ({
                      taglabel: it.cardReaderName,
                      label: (
                        <>
                          <div>{it.cardReaderName}</div>
                          <div>
                            <Typography.Text type="secondary">{it.ordinationName}</Typography.Text>
                          </div>
                          <div>
                            <Typography.Text type="secondary">Tätigkeitsbereich: {it.taetigkeitsBereichName}</Typography.Text>
                          </div>
                        </>
                      ),
                      value: it.id,
                    }))}
                    placeholder="Konfiguration / Kartenlesegerät auswählen"
                    style={{ width: '100%' }}
                    value={configId}
                    onChange={v => {
                      setConfigId(v);
                      setError('');
                    }}
                  />
                  <Button type="primary" onClick={createDialog} disabled={!configId} loading={loading} icon={<LinkOutlined />}>
                    Dialog aufbauen
                  </Button>
                </Flex>
              )}
            </Space>
          )}
          <Typography.Text type="danger">{error}</Typography.Text>
        </Space>
      </Drawer>
    </>
  );
};
